import { CheckCircle2Icon, HomeIcon, Volume2Icon } from "lucide-react";

import type { ReviewQuality } from "@/api/smartReview";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

import type { ReviewResult } from "../_hooks/useSmartReview";

const QUALITY_LABELS: Record<ReviewQuality, string> = {
  again: "다시",
  hard: "어려움",
  normal: "보통",
  easy: "쉬움",
};

type ReviewResultViewProps = {
  results: ReviewResult[];
  onExit: () => void;
  onSpeak: (text: string) => void;
};

export function ReviewResultView({ results, onExit, onSpeak }: ReviewResultViewProps) {
  return (
    <Card className="flex w-full h-full min-h-0 flex-col overflow-hidden bg-primary-background pt-2 sm:pt-5 sm:mx-auto sm:max-w-4xl sm:rounded-2xl sm:h-fit sm:shadow-lg">
      <CardHeader className="flex flex-col items-center gap-2 pt-8 pb-4">
        <div className="flex items-center justify-center size-16 rounded-full bg-primary/10">
          <CheckCircle2Icon className="size-8 text-primary" />
        </div>
        <h2 className="text-xl font-bold text-foreground">복습 완료!</h2>
        <p className="text-sm text-muted-foreground">{results.length}개 단어의 다음 복습 일정을 저장했어요.</p>
      </CardHeader>

      <CardContent className="flex-1 min-h-0 overflow-y-auto px-5 pb-4">
        <div className="flex flex-col gap-2">
          {results.map((result, index) => (
            <div
              key={`${result.question.wordId}-${index}`}
              className="flex flex-row items-center gap-3 rounded-xl bg-white p-4"
            >
              <div className="flex-1 min-w-0">
                <p className="font-bold text-foreground text-base mb-1">{result.question.word}</p>
                <p className="text-sm text-slate-500 font-semibold break-keep">{result.question.meaning}</p>
              </div>

              <div className="shrink-0 text-right">
                <p className="text-sm font-bold text-primary">{QUALITY_LABELS[result.quality]}</p>
                <p className="text-xs text-muted-foreground">
                  {result.schedule.is_graduated ? "복습 완료" : `${result.schedule.interval_days}일 후`}
                </p>
              </div>

              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                aria-label={`${result.question.word} 발음 듣기`}
                onClick={() => onSpeak(result.question.word)}
              >
                <Volume2Icon className="size-5 text-muted-foreground" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>

      <div className="p-5 pt-2">
        <Button type="button" className="w-full rounded-xl py-6 text-base font-semibold" onClick={onExit}>
          <HomeIcon className="size-5 mr-2" />
          홈으로 돌아가기
        </Button>
      </div>
    </Card>
  );
}
